import { defineReactiveModel, Persistable, hasMany } from "vue-app-utils";
import Model from "@/models/model";
import { formatDistanceToNow } from "date-fns";
import { generateLeagueSchedule } from "@/utils/league-schedule";
import { dateToInputString } from "@/utils/time";
import { rankSquads } from "@/utils/rank-squads";
import { resolveBracketSquads, assignBracketSchedule, bracketRoundCount, hasConsolation } from "@/utils/league-bracket";
import { LEAGUE_POINTS_PER_GAME, LEAGUE_POINTS_TO_WIN } from "@/utils/league-constants";
import { goalsBySquadId, isDecided, winningSquadId, racesMatchSchedule, hasAnyScoredGame } from "@/utils/race-utils";
import { finalStandings as computeFinalStandings } from "@/utils/final-standings";

const League = {
  extends: Model,
  mixins: [
    Persistable,
    hasMany("members", {
      orderBy: ["createdAt"]
    }),
    hasMany("squads", {
      orderBy: ["createdAt"]
    }),
    hasMany("locations", {
      orderBy: ["createdAt"]
    }),
    hasMany("races", {
      orderBy: [["createdAt", "desc"]]
    })
  ],
  data () {
    return {
      name: "",
      startDate: new Date(),
      timesEachPairPlays: 2,
      blackoutDates: [],
      bracketSize: 0,
      pointsToWin: LEAGUE_POINTS_TO_WIN,
      pointsPerGame: LEAGUE_POINTS_PER_GAME
    };
  },
  computed: {
    title () {
      return this.name || (this.createdAt ? `Created ${formatDistanceToNow(this.createdAt.toDate())} ago.` : "");
    },
    startDateString () {
      return dateToInputString(this.startDate);
    },
    schedule () {
      return generateLeagueSchedule(this);
    },
    freeAgents () {
      const assignedIds = new Set(
        this.squads.reduce((ids, squad) => ids.concat(squad.memberIds || []), [])
      );
      return this.members.filter(member => !assignedIds.has(member.id));
    },
    rankedSquads () {
      return rankSquads(this.squads);
    },
    bracketRoundCount () {
      return bracketRoundCount(this.bracketSize, this.squads.length);
    },
    roundRobinDecided () {
      const rrRaces = this.races.filter(r => !r.isBracketRace);
      return rrRaces.length > 0 && rrRaces.every(r => r.decided);
    },
    // True once any game in any race has been scored. After that, regenerating races would
    // destroy results, so schedule-shape controls are locked.
    hasScoredGame () {
      return hasAnyScoredGame(this.races);
    },
    // True when the persisted races no longer match the current schedule (settings like
    // timesEachPairPlays, bracketSize, or squads changed). These races must be regenerated.
    racesNeedRegeneration () {
      return !racesMatchSchedule(this.schedule.events, this.races);
    },
    finished () {
      if (this.bracketSize < 2) return this.roundRobinDecided;
      const final = this.races.find(r => r.bracketSlot === "FINAL");
      if (!final || !final.decided) return false;
      if (this.hasConsolationMatch) {
        const third = this.races.find(r => r.bracketSlot === "THIRD");
        return !!third && third.decided;
      }
      return true;
    },
    hasConsolationMatch () {
      return this.bracketSize >= 2 && hasConsolation(Math.min(this.bracketSize, this.squads.length));
    },
    // Final season standings once the league is finished: bracket finishers take 1/2/3/5...
    // by how deep they went (bracket games do not count toward W/L, Pts or Goals), non-bracket
    // squads follow in round-robin order. Matches the round-robin rankings until then.
    finalStandings () {
      return computeFinalStandings({
        rankedSquads: this.rankedSquads,
        bracketSize: this.bracketSize,
        bracketSquads: this.bracketSquads,
        races: this.races,
        finished: this.finished
      });
    },
    // Map of bracketSlot -> [homeSquadId|undefined, awaySquadId|undefined]. Round 1 is seeded
    // from rankings once the round robin finishes; later rounds fill with actual winners as
    // their feeder races get decided. Empty {} until the round robin is complete.
    bracketSquads () {
      const b = this.bracketSize;
      if (b < 2) return {};
      if (!this.roundRobinDecided) return {};
      const ranked = this.rankedSquads.filter(s => s.place !== null);
      if (ranked.length < 2) return {};
      return resolveBracketSquads({
        bracketSize: b,
        seeds: ranked.slice(0, Math.min(b, ranked.length)).map(r => r.squad.id),
        winnerOf: (slotId, homeId, awayId) => {
          const race = this.races.find(r => r.bracketSlot === slotId);
          if (!race) return undefined;
          const totals = goalsBySquadId(race.games, [homeId, awayId]);
          if (!isDecided(totals, this.pointsToWin)) return undefined;
          return winningSquadId(totals, this.pointsToWin);
        }
      });
    },
    // Map of bracketSlot -> { locationId, date, tableNumber, bumped, week } for slots whose
    // finalists are both known. Higher seeds host at their home venue; a game that cannot fit
    // relocates within the week or defers (internal). Slots still TBD get no entry.
    bracketSchedule () {
      const slots = this.bracketSquads;
      if (Object.keys(slots).length === 0) return {};
      const bracketSlots = {};
      for (const event of this.schedule.events) {
        if (!event.bracketSlot) continue;
        const ids = slots[event.bracketSlot];
        if (!ids || !ids[0] || !ids[1]) continue;
        bracketSlots[event.bracketSlot] = {
          week: event.week,
          round: event.round,
          squadIds: [ids[0], ids[1]],
          homeSeed: event.homeSeed
        };
      }
      return assignBracketSchedule({
        bracketSlots,
        squads: this.squads,
        locations: this.locations,
        startDate: this.startDate,
        blackoutDates: this.blackoutDates
      });
    }
  }
};

export default defineReactiveModel(League);
